import { Build, Component, Element, Event, EventEmitter, forceUpdate, h, Method, Prop, State, Watch } from '@stencil/core';

interface ConsentSettings {
  essential: boolean;
  statistic: boolean;
  marketing: boolean;
}

interface MamaConsentManager {
  consentSettings: ConsentSettings;
  updateConsentSettings: (consentSettings: ConsentSettings) => void;
  addListener: (listener: (consentSettings: ConsentSettings) => void) => () => void;
  removeListener: (listener: (consentSettings: ConsentSettings) => void) => void;
  hasCookie: boolean;
  isEssentialEnabled: boolean;
  isStatisticEnabled: boolean;
  isMarketingEnabled: boolean;
}

@Component({
  tag: 'mama-cookie-banner',
  styleUrl: './mama-cookie-banner.scss',
})
export class MamaCookieBanner {
  @Element()
  el!: HTMLMamaCookieBannerElement;

  @Prop()
  forceClosed = false;

  @State()
  visible = false;

  @State()
  consentSettings: ConsentSettings = {
    essential: true,
    statistic: false,
    marketing: false,
  };

  @Event()
  cookieAccepted: EventEmitter;

  private consentManager?: MamaConsentManager = Build.isBrowser ? mamaConsentManager : null;

  @Watch('visible')
  visibleWatcher() {
    if (this.visible) {
      this.el.removeAttribute('hidden');
    } else {
      this.el.setAttribute('hidden', 'hidden');
    }
  }

  @Method()
  async open() {
    this.visible = true;
  }

  @Method()
  async close() {
    this.visible = false;
  }

  settingsChangedListener = (consentSettings: ConsentSettings) => {
    this.consentSettings = Object.assign(this.consentSettings, consentSettings);
    forceUpdate(this.el);
  };

  componentWillLoad() {
    if (navigator.userAgent.match(/Chrome-Lighthouse/i) || !Build.isBrowser) {
      return;
    }
    this.consentManager.addListener(this.settingsChangedListener);
    this.consentSettings = Object.assign(this.consentSettings, this.consentManager.consentSettings);
    this.visible = !this.consentManager.hasCookie;
    if (this.forceClosed) {
      this.visible = false;
    }
    this.visibleWatcher();
  }

  disconnectedCallback() {
    this.consentManager.removeListener(this.settingsChangedListener);
  }

  save(event: MouseEvent) {
    event.preventDefault();
    this.consentManager.updateConsentSettings(this.consentSettings);
    this.close();
  }

  saveAll(event: MouseEvent) {
    event.preventDefault();
    this.consentManager.updateConsentSettings({
      essential: true,
      statistic: true,
      marketing: false,
    });
    this.close();
  }

  handleChange(event, consentName: string) {
    this.consentSettings[consentName] = event.target.checked;
  }

  renderEssential() {
    if (this.el.querySelector('[slot=label-essential]')) {
      return [
        <label class="custom-control custom-checkbox custom-control-inline" htmlFor="cookie-consent-essential">
          <input id="cookie-consent-essential" type="checkbox" class="custom-control-input" value="1" checked disabled />
          <span class="custom-control-indicator custom-control-input-checked" />
          <slot name="label-essential" />
        </label>,
      ];
    }
  }

  renderStatistic() {
    if (this.el.querySelector('[slot=label-statistic]')) {
      return [
        <label class="custom-control custom-checkbox custom-control-inline" htmlFor="cookie-consent-statistic">
          <input
            id="cookie-consent-statistic"
            type="checkbox"
            class="custom-control-input"
            value="1"
            checked={this.consentSettings.statistic}
            onInput={event => this.handleChange(event, 'statistic')}
          />
          <span class="custom-control-indicator custom-control-input-checked" />
          <slot name="label-statistic" />
        </label>,
      ];
    }
  }
  renderMarketing() {
    if (this.el.querySelector('[slot=label-marketing]')) {
      return [
        <label class="custom-control custom-checkbox custom-control-inline" htmlFor="cookie-consent-marketing">
          <input
            id="cookie-consent-marketing"
            type="checkbox"
            class="custom-control-input"
            value="1"
            checked={this.consentSettings.marketing}
            onInput={event => this.handleChange(event, 'marketing')}
          />
          <span class="custom-control-indicator custom-control-input-checked" />
          <slot name="label-marketing" />
        </label>,
      ];
    }
  }

  render() {
    return [
      <div class="modal modal-cookie">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <h2 class="modal-title">
              <slot name="headline" />
            </h2>
            <p>
              <slot name="text" />
            </p>
            <div class="checkbox-inline">
              {this.renderEssential()}
              {this.renderStatistic()}
              {this.renderMarketing()}
            </div>
            <div class="actions">
              <a class="btn btn-primary icon-chevron-right icon-right" href="#" role="button" onClick={event => this.saveAll(event)}>
                <slot name="button-saveall" />
              </a>
              <a class="btn btn-dark-gray icon-chevron-right icon-right" href="#" role="button" onClick={event => this.save(event)}>
                <slot name="button-save" />
              </a>
            </div>
            <p>
              <slot name="footer" />
            </p>
          </div>
        </div>
      </div>,
      <div class="modal-backdrop" />,
    ];
  }
}
